
import { makeStyles } from 'tss-react/mui';
import React, { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ApiButton from 'web-library/stories/atom/Button/ApiButton';
import Button from 'web-library/stories/atom/Button/Button';
import Grid from 'web-library/stories/atom/Grid/Grid';
import Box from 'web-library/stories/atom/Box/Box';
import ErrorInfo from 'web-library/stories/molecule/ErrorInfo/ErrorInfo';
import ContentTemplate from 'web-library/stories/template/ContentTemplate/ContentTemplate';
import ControlledDatePicker from 'web-library/stories/atom/DatePicker/ControlledDatePicker';
import SectionHeader from 'web-library/stories/molecule/SectionHeader/SectionHeader';
import TextFieldForm from "web-library/stories/atom/TextField/TextFieldForm";
import { useGetCaptchaSiteKey } from '../../../api-refactored/user/useGetCaptchaSiteKey';
import { usePostCaptchaVerifyToken } from '../../../api-refactored/user/usePostCaptchaVerifyToken';
import { useWOGAA } from '../../../hooks/wogaa';

import RouteKey from '../../../routes/key';
import { useAccountDetailForm } from '../logic/useAccountDetailForm';

const Captcha = lazy(() => import('web-library/stories/atom/Captcha/Captcha'));

const useStyles = makeStyles()((theme) =>
({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
  },

  submitButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  actionButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  captcha: {
    paddingBottom: theme.spacing(4),
  },
  errorAsterisk: {
    color: theme.palette.error.main,
  },
}));

const RequiredIndicator = () => {
  const { classes } = useStyles();
  return <span className={classes.errorAsterisk}>&nbsp;*</span>;
};

const AccountActivationDetail = ({ next }: { next: any }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { auth: authRoute } = RouteKey.child;

  const { form, accountDetails, submitError, accountActivationMutation, showCaptcha } =
    useAccountDetailForm(next);

  const { handleSubmit, formState: { errors }, setValue, control } = form;

  const [isBot, setIsBot] = useState<boolean>(false);
  const checkIsBot = (result: boolean) => setIsBot(result);
  const captchaVerifyTokenMutation = usePostCaptchaVerifyToken({
    onSuccess: (data) => setIsBot(data ? data.isBot : true),
    onError: () => setIsBot(true),
  });

  const { transactionStart } =  useWOGAA('myexamsseab-6476');

  /** WOGAA transaction tracking start
   * */
  transactionStart();

  return (
    (<ContentTemplate>
      <SectionHeader title="Account Details" />
      <Box className={classes.container}>
        <form
          noValidate
          onSubmit={handleSubmit((formData: any) => {
            accountDetails(formData);
          })}
        >
          <TextFieldForm
              form={form}
            label={
              <>
                {t('admin->>AccountActivationDetail.Username')}
                <RequiredIndicator />
              </>
            }
            name="username"
            placeholder="Please enter Username"
            type="text"
            error={!!errors.username}
            helperText={errors?.username?.message?.toString()}
            className={classes.formElementContainer}
            fullWidth
          />

          <ControlledDatePicker control={control}
            label={
              <>
                {t('admin->>AccountActivationDetail.DateOfBirth')}
                <RequiredIndicator />
              </>
            }
            onChange={(date) => {
              setValue('dateOfBirth', date || '');
            }}
            name="dateOfBirth"
            disableFuture
            autoOk
            error={!!errors.dateOfBirth}
            helperText={errors?.dateOfBirth?.message?.toString()}
            inputVariant="filled"
            className={classes.formElementContainer}
          />

          {showCaptcha && (
            <Suspense fallback={null}>
              <Captcha
                {...{
                  id: 'activate-account-captcha',
                  className: classes.captcha,
                  checkIsBot,
                  captchaSiteKeyMutation: useGetCaptchaSiteKey,
                  captchaVerifyTokenMutation,
                }}
              />
            </Suspense>
          )}

          <Grid container direction="row" justify="flex-start">
            <Button
              variant="outlined"
              className={classes.actionButton}
              onClick={() => {
                history.push(`/${authRoute}/`);
              }}
            >
              {t('admin->>AccountActivationDetail.Back')}
            </Button>

            <ApiButton
              className={classes.actionButton}
              status={accountActivationMutation.status}
              type="submit"
              disabled={isBot}
            >
              {t('admin->>AccountActivationDetail.ConfirmDetails')}
            </ApiButton>
          </Grid>
          <ErrorInfo label={submitError || ''} />
        </form>
      </Box>
    </ContentTemplate>)
  );
};

export default AccountActivationDetail;
